import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { FrameWithPolygon, type FrameBox } from '@/components/ui/frame-with-polygon';
import { PolygonCrop } from '@/components/projection/PolygonCrop';
import { TableConfirmedState, TableProofImage, useTableProof, useTableProofTime, type LiveTableProof } from '@/components/dashboard/table-proof';
import { useT } from '@/contexts/I18nContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';

/** Client-side crop: cut the polygons' region out of the (fresh) full frame
 *  instead of showing a stored crop image that may lag far behind. */
export interface LightboxCropSpec {
  frameSrc: string;
  polygons: number[][][];
}

interface ImageLightboxProps {
  /** Table dashboard only. Caller preserves the open gallery IDs/order. */
  liveTables?: LiveTableProof[];
  /** Stored crop image per index (null when the unit only has a frame). */
  srcs: (string | null)[];
  index: number;
  alt?: string;
  /** Optional per-index captions shown under the image (e.g. unit name +
   *  status). An array, not a single string: the lightbox freezes its props on
   *  open, so a caption computed by the parent for the CURRENT index would
   *  stay stuck on the open-time value while navigation switches images. */
  captions?: (string | null)[];
  /**
   * Full-frame view (F5): per-index full camera frame URL + target polygon(s)
   * in 0..1 coords. When present for the current index, the lightbox shows the
   * full frame with the target OUTLINED, and a press-and-HOLD reveals the tight
   * crop; releasing returns to the outlined frame. null entries fall back to
   * the plain crop.
   */
  frameSrcs?: (string | null)[];
  polygonsList?: (number[][][] | null)[];
  /** Optional per-index detected-box overlay (#158, staff-only "рисовать
   *  боксы"). Presence alone is enough to switch an index into full-frame
   *  mode even with no polygon outline (`polygonsList` may be omitted/empty)
   *  — the caller owns the checkbox/permission gate, this prop just carries
   *  the data through to FrameWithPolygon. */
  boxesList?: (FrameBox[] | null)[];
  /** Зелёные контуры без заливки (полка на кропе стеллажа), по индексу фото. */
  outlinesList?: (number[][][] | null)[];
  /** Per-index client-side crop. When present it REPLACES `srcs[index]` as the
   *  crop visual — same fresh frame as the hold-peek, never a stale image. */
  cropSpecs?: (LightboxCropSpec | null)[];
  /** Which visual leads: 'frame' (default) shows the outlined frame with the
   *  crop on hold; 'crop' shows the crop with the outlined frame on hold —
   *  follows the dashboard's current display mode. */
  primary?: 'frame' | 'crop';
  /** Optional hint shown when a press-and-hold peek is available. */
  holdHint?: string;
  /** Второй кадр рядом с первым (очередь и бар с разных камер, L2) — в обычном
   *  режиме, без оверлеев; подписи под каждым кадром — pairLabels. */
  pairSrcs?: (string | null)[];
  pairLabels?: ([string, string] | null)[];
  onClose: () => void;
  onNavigate: (i: number) => void;
}

// How long the pointer must stay down (without moving) before the crop reveals
// — long enough that a tap or the start of a swipe never flips it.
const HOLD_MS = 350;
const MOVE_CANCEL_PX = 12;
// Horizontal travel that counts as a navigate-swipe (touch only).
const SWIPE_NAV_PX = 50;
// Left/right screen fraction that counts as a tap-to-navigate zone (touch
// only) — standard photo-viewer pattern: tap the outer edges to page through
// the series, tap the middle to close.
const TAP_ZONE_FRACTION = 0.3;

export function ImageLightbox({
  liveTables,
  srcs: srcsProp,
  index,
  alt: altProp,
  captions: captionsProp,
  frameSrcs: frameSrcsProp,
  polygonsList: polygonsListProp,
  boxesList: boxesListProp,
  outlinesList: outlinesListProp,
  cropSpecs: cropSpecsProp,
  primary = 'frame',
  holdHint,
  pairSrcs: pairSrcsProp,
  pairLabels: pairLabelsProp,
  onClose,
  onNavigate,
}: ImageLightboxProps) {
  const { t } = useT();
  const frameTime = useTableProofTime();
  const liveTable = liveTables?.[index];
  // Freeze the gallery the moment it opens. The dashboard refreshes its data
  // every 10s, which swapped these URLs underneath an open lightbox → the image
  // you were examining re-decoded and blinked (and silently changed to a newer
  // capture). index/onClose/onNavigate stay live so navigation still works on the
  // frozen snapshot; close+reopen to pick up newer frames.
  const frozen = useRef<{
    srcs: (string | null)[];
    alt?: string;
    captions?: (string | null)[];
    frameSrcs?: (string | null)[];
    polygonsList?: (number[][][] | null)[];
    boxesList?: (FrameBox[] | null)[];
    outlinesList?: (number[][][] | null)[];
    cropSpecs?: (LightboxCropSpec | null)[];
    pairSrcs?: (string | null)[];
    pairLabels?: ([string, string] | null)[];
  } | null>(null);
  if (frozen.current === null) {
    frozen.current = {
      srcs: srcsProp, alt: altProp, captions: captionsProp,
      frameSrcs: frameSrcsProp, polygonsList: polygonsListProp,
      boxesList: boxesListProp, outlinesList: outlinesListProp,
      cropSpecs: cropSpecsProp,
      pairSrcs: pairSrcsProp, pairLabels: pairLabelsProp,
    };
  }
  const { srcs, alt, captions, frameSrcs, polygonsList, boxesList, outlinesList, cropSpecs, pairSrcs, pairLabels } = liveTables ? {
    srcs: srcsProp, alt: liveTable?.name, captions: captionsProp,
    frameSrcs: frameSrcsProp, polygonsList: polygonsListProp, boxesList: boxesListProp,
    outlinesList: outlinesListProp, cropSpecs: cropSpecsProp, pairSrcs: pairSrcsProp, pairLabels: pairLabelsProp,
  } : frozen.current;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
      if (e.key === 'ArrowRight' && index < srcs.length - 1) onNavigate(index + 1);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onNavigate, index, srcs.length]);

  const src = srcs[index];
  const caption = liveTable ? liveTable.name : captions?.[index] ?? null;
  const frame = frameSrcs?.[index] ?? null;
  const polys = polygonsList?.[index] ?? null;
  const boxes = boxesList?.[index] ?? null;
  const outlines = outlinesList?.[index] ?? undefined;
  const spec = cropSpecs?.[index] ?? null;
  const pairSrc = pairSrcs?.[index] ?? null;
  const pairLabel = pairLabels?.[index] ?? null;
  // A staff-only boxes overlay (#158) also earns full-frame mode on its own,
  // even with no polygon outline for this index.
  const fullFrame = !!frame && ((!!polys && polys.length > 0) || !!boxes);
  // 'crop' leads when asked to (and there is a crop to show) — and also when
  // the outlined frame CAN'T lead (no frame/polygons) but a client-side crop
  // exists, so we never fall through to a null <img>.
  const cropFirst = (primary === 'crop' || !fullFrame) && (!!spec || !!src);
  // Пара кадров — только в обычном режиме: с оверлеем боксов кадр один.
  const pairMode = !!pairSrc && !!src && !fullFrame && !spec;

  const coarse = useMediaQuery('(pointer: coarse)');

  // ONE pointer controller for the whole surface so the gestures never fight
  // each other (the old split between a backdrop onClick and the container's own
  // handlers let a tap near an edge hit a nav arrow, and a pinch start register
  // as a click → navigate). Disambiguation on pointer-up:
  //   • a second finger / a control (X, arrows) → ignore (let pinch-zoom or the
  //     button do its thing — never navigate or close),
  //   • full-frame press-and-HOLD (no move) → peek the crop; release returns,
  //   • a clear horizontal swipe (touch) → prev/next,
  //   • a clean tap (no move, no hold) → close.
  const [holding, setHolding] = useState(false);
  const tableMedia = useTableProof(liveTable, holding ? (primary === 'crop' ? 'frame' : 'crop') : primary);
  const holdTimer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const ignore = useRef(false); // pinch or control — this gesture does nothing
  const clearHold = () => {
    if (holdTimer.current) { window.clearTimeout(holdTimer.current); holdTimer.current = null; }
  };
  const onPointerDown = (e: React.PointerEvent) => {
    // Правый/средний клик мышью — не жест лайтбокса: правый клик открывает
    // меню «Копировать изображение» и не должен закрывать просмотр (AI12.4).
    if (e.pointerType === 'mouse' && e.button !== 0) {
      ignore.current = true;
      clearHold();
      start.current = null;
      return;
    }
    // A second finger (pinch-zoom) or a press on a control: disarm this gesture.
    if (!e.isPrimary || (e.target as Element | null)?.closest?.('button')) {
      ignore.current = true;
      clearHold();
      start.current = null;
      setHolding(false);
      return;
    }
    ignore.current = false;
    start.current = { x: e.clientX, y: e.clientY };
    clearHold();
    // Press-and-hold to peek the crop is a full-frame affordance — on both
    // desktop AND touch. (It used to be gated to desktop-only here because a
    // close-tap held a hair too long flipped to the crop instead of closing;
    // that's handled below by returning early on `wasHolding` — releasing a
    // peek never closes/navigates — so touch gets the same hold-to-peek UX.)
    if (fullFrame) {
      // Warm the browser's image cache the instant the finger goes down, not
      // when HOLD_MS elapses — the counterpart element (FrameWithPolygon /
      // the crop overlay) only mounts once `holding` flips true, so without
      // this a slow mobile connection can still be decoding when the peek
      // renders, and the previous layer stays visible through the semi-
      // transparent overlay — reads as "the crop came up fullscreen, not the
      // full photo" (reported by Alexey, #298).
      if (frame) { const img = new Image(); img.src = frame; }
      if (spec) { const img = new Image(); img.src = spec.frameSrc; }
      holdTimer.current = window.setTimeout(() => setHolding(true), HOLD_MS);
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = start.current;
    if (!s || ignore.current || holding) return;
    if (Math.abs(e.clientX - s.x) > MOVE_CANCEL_PX || Math.abs(e.clientY - s.y) > MOVE_CANCEL_PX) {
      clearHold(); // a drag is not a hold
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const s = start.current;
    const wasHolding = holding;
    const wasIgnored = ignore.current;
    clearHold();
    start.current = null;
    setHolding(false);
    ignore.current = false;
    if (wasIgnored || !s) return;
    if (wasHolding) return; // released a peek → don't close
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    // Touch horizontal swipe → navigate (the on-screen arrows are hidden there).
    if (coarse && Math.abs(dx) > SWIPE_NAV_PX && Math.abs(dx) > Math.abs(dy) * 1.2) {
      if (dx < 0 && index < srcs.length - 1) onNavigate(index + 1);
      else if (dx > 0 && index > 0) onNavigate(index - 1);
      return;
    }
    // Touch tap (not a swipe): the outer left/right edges page through the
    // series like a standard photo viewer, the middle closes. Uses the
    // release position, not the on-screen arrows (hidden on touch).
    if (coarse) {
      const w = window.innerWidth || 1;
      const frac = e.clientX / w;
      if (frac < TAP_ZONE_FRACTION && index > 0) { onNavigate(index - 1); return; }
      if (frac > 1 - TAP_ZONE_FRACTION && index < srcs.length - 1) { onNavigate(index + 1); return; }
    }
    // Anything else — a middle tap, or a small/vertical drag that wasn't a
    // clear swipe — closes. (Forgiving on purpose: "tap elsewhere to close"
    // must win even when the finger isn't perfectly still.)
    onClose();
  };
  const onPointerCancel = () => { clearHold(); start.current = null; setHolding(false); ignore.current = false; };
  useEffect(() => () => clearHold(), []);
  // Reset hold + crop-loaded state whenever the image changes.
  const [cropLoaded, setCropLoaded] = useState(false);
  useEffect(() => { setHolding(false); clearHold(); }, [index]);
  useEffect(() => { setCropLoaded(false); }, [holding, index]);

  // Plain-mode loader (full-frame mode has its own loader inside FrameWithPolygon).
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);
  if (!liveTable && !src && !spec && !fullFrame) return null;
  if (typeof document === 'undefined') return null;

  // Картинка для нативного меню «Копировать изображение» на компьютере: кадр
  // рисуется внутри <svg>, а у svg-image в меню нет пункта копирования.
  // Прозрачный <img> того же кадра поверх — жесты всплывают к контейнеру как
  // раньше (AI12.4).
  const copySrc = holding
    ? (cropFirst ? frame : (spec ? null : src))
    : (cropFirst ? (spec ? null : src) : frame);
  const copyLayer = !coarse && copySrc && !pairMode ? (
    <img
      src={copySrc}
      alt=""
      draggable={false}
      className="absolute inset-0 z-[1] h-full w-full object-contain opacity-0"
    />
  ) : null;

  // Портал в body: лайтбокс монтируется внутри карусели дашборда смены, а у
  // её страниц transform — от такого предка position:fixed считается не от
  // экрана, и на телефоне просмотр выходил в три ширины экрана с крестиком за
  // краем (AI12.3). Синтетические события React всё равно всплывают по дереву
  // компонентов, поэтому stopPropagation ниже работает как раньше.
  return createPortal(
    <div
      // Inset the image from the screen edges (Alexey 2026-07-21: filling 100%
      // was overwhelming) — a social-app-style margin. Corner controls/caption
      // are absolutely positioned, so they stay near the edges.
      className="fixed inset-0 z-50 flex select-none items-center justify-center bg-black/80 p-4 sm:p-8 md:p-12"
      // pinch-zoom (not none) so two fingers still zoom the image on mobile;
      // single-finger pointer events are ours (tap-close / swipe-nav / hold-peek).
      // callout:none + contextmenu suppression kill the long-press image menu.
      style={{ touchAction: 'pinch-zoom', WebkitTouchCallout: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerCancel}
      onPointerCancel={onPointerCancel}
      // The dashboard behind the lightbox has its own swipe carousels (vertical
      // photo pager + horizontal tab carousel, both driven by native Touch
      // events on ancestor elements — the lightbox is mounted INSIDE their DOM
      // subtree, not a sibling/portal). Our own gestures are handled above via
      // Pointer events, but stopping Pointer propagation does nothing to a
      // parallel Touch event stream: without this, a swipe meant to page
      // through the lightbox's photos also bubbled up and flipped the page/tab
      // underneath it. Stop Touch events here so the lightbox is the only thing
      // that reacts to a swipe while it's open.
      onTouchStart={e => e.stopPropagation()}
      onTouchMove={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
      onTouchCancel={e => e.stopPropagation()}
      // Долгое нажатие на телефоне не должно открывать меню картинки (там это
      // жест «подсмотреть»); на компьютере меню нужно для копирования кадра.
      onContextMenu={e => { if (coarse) e.preventDefault(); }}
    >
      <button
        type="button"
        className={coarse
          ? 'absolute right-3 z-10 rounded-full bg-black/60 p-2.5 text-white'
          : 'absolute right-4 top-4 z-10 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60'}
        style={coarse ? { top: 'max(0.75rem, env(safe-area-inset-top))' } : undefined}
        onClick={onClose}
        aria-label="Close"
      >
        <X className={coarse ? 'size-6' : 'size-5'} />
      </button>

      {/* Arrows are a desktop affordance; on touch they stole taps meant to
          close, so there we navigate by swiping instead. */}
      {!coarse && srcs.length > 1 && index > 0 && (
        <button
          type="button"
          className="absolute left-4 z-10 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60"
          onClick={e => { e.stopPropagation(); onNavigate(index - 1); }}
          aria-label="Previous"
        >
          <ChevronLeft className="size-5" />
        </button>
      )}

      {liveTable ? (
        <div className="relative flex h-full w-full items-center justify-center" data-live-table={String(liveTable.id)}>
          <TableProofImage image={tableMedia.image} loading={tableMedia.loading} name={liveTable.name} />
          {tableMedia.image && tableMedia.image.kind !== 'frameCrop' && !coarse && <img
            src={tableMedia.image.src} alt="" draggable={false} className="absolute inset-0 h-full w-full object-contain opacity-0" />}
        </div>
      ) : pairMode ? (
        // Два кадра рядом (очередь и бар с разных камер): каждый со своим
        // временем, чтобы разрыв между ними был виден, а не скрыт.
        <div className="flex h-full w-full items-center justify-center gap-2">
          {([[src!, pairLabel?.[0]], [pairSrc!, pairLabel?.[1]]] as [string, string | undefined][]).map(([s, l], i) => (
            <figure key={i} className="flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-1">
              <img src={s} alt={alt ?? ''} draggable={false} className="min-h-0 max-h-full max-w-full object-contain" />
              {l && <figcaption className="text-xs text-white/70">{l}</figcaption>}
            </figure>
          ))}
        </div>
      ) : cropFirst ? (
        // Crop-first (dashboard crop view): the crop leads — the client-side
        // PolygonCrop when available (fresh frame), else the stored crop image —
        // and press-and-hold reveals the outlined full frame.
        // Fullscreen: fills the fixed-inset-0 backdrop edge to edge; the image
        // itself letterboxes via object-contain, only the caption below stays
        // clear of the bottom tab bar.
        <div className="relative flex h-full w-full items-center justify-center">
          {spec ? (
            <PolygonCrop
              frameSrc={spec.frameSrc}
              polygons={spec.polygons}
              alt={alt ?? ''}
              className="h-full w-full object-contain"
            />
          ) : (
            <>
              {!loaded && (
                <Loader2 className="pointer-events-none absolute size-8 animate-spin text-white/80" aria-hidden />
              )}
              <img
                src={src!}
                alt={alt ?? ''}
                draggable={false}
                className={`h-full w-full object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
              />
            </>
          )}
          {copyLayer}
          {holding && fullFrame && (
            <span className="absolute inset-0 bg-black/85">
              <FrameWithPolygon src={frame!} polygons={polys ?? []} boxes={boxes ?? undefined} outlines={outlines} alt={alt ?? ''} strokeWidth={2.5} />
            </span>
          )}
        </div>
      ) : fullFrame ? (
        // Full frame + outlined target; press-and-hold overlays the crop. The
        // frame stays MOUNTED under the overlay (we no longer swap it out for the
        // crop) so releasing the hold doesn't re-decode it — it was reloading the
        // frame on every peek. (No holdPrevious here: on navigate the src changes
        // to a DIFFERENT unit, where a clean loader is correct.) Fullscreen,
        // same rationale as the crop-first branch above.
        <div className="relative flex h-full w-full items-center justify-center">
          <FrameWithPolygon src={frame!} polygons={polys ?? []} boxes={boxes ?? undefined} outlines={outlines} alt={alt ?? ''} strokeWidth={2.5} />
          {!holding && copyLayer}
          {holding && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/85">
              {spec ? (
                <PolygonCrop
                  frameSrc={spec.frameSrc}
                  polygons={spec.polygons}
                  alt={alt ?? ''}
                  className="h-full w-full object-contain"
                />
              ) : (
                <>
                  {!cropLoaded && (
                    <Loader2 className="pointer-events-none absolute size-8 animate-spin text-white/80" aria-hidden />
                  )}
                  <img
                    src={src ?? undefined}
                    alt={alt ?? ''}
                    draggable={false}
                    onLoad={() => setCropLoaded(true)}
                    onError={() => setCropLoaded(true)}
                    className={`h-full w-full object-contain transition-opacity duration-150 ${cropLoaded ? 'opacity-100' : 'opacity-0'}`}
                  />
                </>
              )}
            </span>
          )}
        </div>
      ) : (
        <>
          {!loaded && (
            <Loader2 className="pointer-events-none absolute size-8 animate-spin text-white/80" aria-hidden />
          )}
          <img
            src={src!}
            alt={alt ?? ''}
            draggable={false}
            className={`max-h-full max-w-full object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        </>
      )}

      {!coarse && srcs.length > 1 && index < srcs.length - 1 && (
        <button
          type="button"
          className="absolute right-4 z-10 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60"
          onClick={e => { e.stopPropagation(); onNavigate(index + 1); }}
          aria-label="Next"
        >
          <ChevronRight className="size-5" />
        </button>
      )}

      {(caption || srcs.length > 1 || (fullFrame && holdHint)) && (
        <div
          className="pointer-events-none absolute inset-x-0 flex flex-col items-center gap-0.5 px-4 text-center text-white/80"
          // The photo/backdrop is fullscreen (including under the floating
          // mobile tab bar, which sits above it in z-order), but the caption
          // text must stay clear of that bar rather than sit under/behind it.
          // `--dashboard-tab-bar-clear` is published by DashboardTabBar's
          // resize-observed measurement; falls back to a safe-area-only inset
          // when the bar isn't mounted (desktop, or no tab bar this visit).
          style={{ bottom: 'max(0.5rem, var(--dashboard-tab-bar-clear, env(safe-area-inset-bottom)))' }}
        >
          <span className="text-sm font-medium">
            {caption}
            {liveTable && <> · <TableConfirmedState source={liveTable} /></>}
            {liveTable && tableMedia.image && <span data-table-photo-time={tableMedia.image.capturedAt ?? ''}>
              {' · '}{frameTime(tableMedia.image.capturedAt)}
            </span>}
          </span>
          {fullFrame && holdHint && !holding && (
            <span className="text-xs text-white/60">{holdHint}</span>
          )}
          {srcs.length > 1 && (
            <span className="text-xs text-white/60">
              {index + 1} / {srcs.length}
            </span>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
